import { Button } from "components/ui/Button";
import type { FC, ReactNode } from "react";

export type ConfirmDialogVariant = "default" | "destructive";

export type ConfirmDialogProps = {
  /** モーダル表示状態 */
  isOpen: boolean;
  /** 見出し (例: "削除の確認") */
  title?: string;
  /** 本文。文字列または任意の ReactNode */
  message: ReactNode;
  /** OK ボタンのラベル (デフォルト: "実行") */
  confirmText?: string;
  /** キャンセルボタンのラベル (デフォルト: "キャンセル") */
  cancelText?: string;
  /** OK ボタンの variant。"destructive" を指定すると赤系で警告 */
  variant?: ConfirmDialogVariant;
  /** OK 押下時 */
  onConfirm: () => void;
  /** キャンセル / バックドロップ押下時 */
  onCancel: () => void;
};

/**
 * 共通確認モーダル。``window.confirm`` の代替として利用する。
 *
 * 直接使うより ``useConfirm()`` フック (同階層) の Promise API のほうが
 * async/await で書けて簡潔なため、特別な理由がない限りそちらを推奨する。
 *
 * @param {ConfirmDialogProps} props モーダル状態と表示文言
 * @return {JSX.Element|null} モーダル本体 (isOpen=false のときは null)
 */
export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = "実行",
  cancelText = "キャンセル",
  variant = "default",
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;
  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-md">
        {title && <h3 className="font-bold text-lg mb-3">{title}</h3>}
        <div className="text-sm whitespace-pre-wrap">{message}</div>
        <div className="modal-action">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onCancel} />
    </div>
  );
};
